// src/support/chatEngine.js
// Keyword-matching chat engine with FAQ auto-reply and ticket escalation.

import knowledgeBase, { quickReplies } from './knowledgeBase';

/**
 * Tokenise and normalise user input for matching.
 */
function normalise(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
}

/**
 * Score a knowledge base entry against user input.
 * Returns a number 0..1 representing match confidence.
 */
function scoreEntry(entry, input) {
  const norm = normalise(input);
  const words = norm.split(/\s+/);
  let best = 0;

  for (const kw of entry.keywords) {
    const kwNorm = normalise(kw);

    // Exact phrase match — highest confidence
    if (norm.includes(kwNorm)) {
      const ratio = kwNorm.length / Math.max(norm.length, 1);
      best = Math.max(best, 0.6 + ratio * 0.4); // 0.6–1.0
      continue;
    }

    // Word overlap — partial match
    const kwWords = kwNorm.split(/\s+/);
    const overlap = kwWords.filter((w) => words.includes(w)).length;
    if (overlap > 0) {
      const ratio = overlap / kwWords.length;
      best = Math.max(best, ratio * 0.55); // up to 0.55
    }
  }

  return best;
}

/**
 * Find the best matching FAQ entry for user input.
 * Returns { entry, score } or null if no match meets threshold.
 */
export function findBestMatch(input, threshold = 0.35) {
  if (!input || !input.trim()) return null;

  let bestEntry = null;
  let bestScore = 0;

  for (const entry of knowledgeBase) {
    const score = scoreEntry(entry, input);
    if (score > bestScore) {
      bestScore = score;
      bestEntry = entry;
    }
  }

  if (bestScore >= threshold && bestEntry) {
    return { entry: bestEntry, score: bestScore };
  }

  return null;
}

/**
 * Find multiple related entries (for "did you mean?" suggestions).
 */
export function findRelated(input, limit = 3, threshold = 0.2) {
  if (!input || !input.trim()) return [];

  const scored = knowledgeBase
    .map((entry) => ({ entry, score: scoreEntry(entry, input) }))
    .filter((r) => r.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored;
}

// ── Chat message types ──
export const MSG_USER = 'user';
export const MSG_BOT = 'bot';
export const MSG_SYSTEM = 'system';

// ── Escalation detection ──
const ESCALATION_KEYWORDS = [
  'talk to human', 'talk to a human', 'real person', 'speak to someone', 'agent', 'operator',
  'live chat', 'create ticket', 'create a ticket', 'open ticket', 'open a ticket', 'submit ticket', 'submit a ticket', 'human support',
  'support team', 'talk to support', 'contact support',
  'not helpful', 'didnt help', "didn't help", 'still need help', 'escalate',
];

/**
 * Check if the user wants to escalate to a human / create a ticket.
 */
export function shouldEscalate(input) {
  const norm = normalise(input);
  return ESCALATION_KEYWORDS.some((kw) => norm.includes(normalise(kw)));
}

/**
 * Count consecutive messages where the bot couldn't find a match.
 * After N misses, auto-suggest escalation.
 */
const AUTO_ESCALATE_AFTER = 2;

/**
 * Process a user message and return the bot's response(s).
 *
 * @param {string} input          — user's message text
 * @param {Array}  history        — previous messages [{type, text, ...}]
 * @returns {Array<{type: string, text: string, entry?: object, escalate?: boolean, suggestions?: string[]}>}
 */
export function processMessage(input, history = []) {
  const responses = [];

  // Check escalation intent
  if (shouldEscalate(input)) {
    responses.push({
      type: MSG_BOT,
      text: "I'll connect you with our support team. Please describe your issue in the form below and we'll get back to you shortly.",
      escalate: true,
    });
    return responses;
  }

  // Try FAQ match
  const match = findBestMatch(input);

  if (match && match.score >= 0.5) {
    // Strong match — give the answer
    responses.push({
      type: MSG_BOT,
      text: match.entry.answer,
      entry: match.entry,
    });
    responses.push({
      type: MSG_BOT,
      text: 'Did this help? If not, I can create a support ticket for you.',
      suggestions: ['Yes, thanks!', 'Create a ticket', 'I have another question'],
    });
    return responses;
  }

  if (match && match.score >= 0.35) {
    // Weak match — ask for confirmation
    const related = findRelated(input, 3, 0.25);
    const options = related.map((r) => r.entry.question);
    responses.push({
      type: MSG_BOT,
      text: "I found some topics that might help. Did you mean one of these?",
      suggestions: [...options, 'None of these — create a ticket'],
    });
    return responses;
  }

  // No match — count consecutive misses
  let consecutiveMisses = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].type === MSG_BOT && history[i].noMatch) {
      consecutiveMisses++;
    } else if (history[i].type === MSG_USER) {
      break;
    }
  }

  if (consecutiveMisses + 1 >= AUTO_ESCALATE_AFTER) {
    responses.push({
      type: MSG_BOT,
      text: "I'm having trouble finding an answer for you. Let me connect you with our support team — they'll be able to help.",
      escalate: true,
    });
  } else {
    responses.push({
      type: MSG_BOT,
      text: "I'm not sure I understand. Could you rephrase your question? Or choose a topic below.",
      noMatch: true,
      suggestions: quickReplies,
    });
  }

  return responses;
}

/**
 * Get the initial greeting messages when chat opens.
 */
export function getGreeting() {
  return [
    {
      type: MSG_BOT,
      text: "Hi! I'm the PGT Support Assistant. I can help with tickets, wallet issues, refunds, and more. What can I help you with?",
      suggestions: quickReplies,
    },
  ];
}

export { quickReplies };
